import React, { useEffect, useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { useFonts } from '@folklore/fonts';
import { useLocation } from 'react-router-dom';
import anime from 'animejs';

import { useNewsletter } from '../../contexts/NewsletterContext';
import { usePopup } from '../../contexts/PopupContext';

import Header from '../partials/Header';
import Footer from '../partials/Footer';
import Background from '../partials/Background';
import LoadingBar from '../partials/LoadingBar';
import EventNotebook from '../partials/EventNotebook';
import MenuMobile from '../menus/Mobile';
import BurgerButton from '../buttons/Burger';
import CloseButton from '../buttons/Close';
import Modals from '../modals/Modals';

import MainMenu from '../menus/Main';
import SplashPage from '../pages/Splash';

import { getLightBackground, getDarkBackground, removeHex } from '../../lib/utils';

import {
    setMenuOpen as setMenuOpenAction,
    setSplash as setSplashAction,
} from '../../actions/SiteActions';

import * as AppPropTypes from '../../lib/PropTypes';

import styles from '../../../styles/layout/main.scss';

const propTypes = {
    page: AppPropTypes.page,
    transitionTheme: PropTypes.string,
    splash: PropTypes.bool.isRequired,
    setSplash: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
    isPrerender: PropTypes.bool,
    fonts: PropTypes.shape({
        google: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
        custom: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
    }),
    menus: AppPropTypes.menus,
    menuOpen: PropTypes.bool,
    color: AppPropTypes.color,
    theme: PropTypes.string,
    inTransition: PropTypes.bool,
    setMenuOpen: PropTypes.func.isRequired,
    donationUrl: PropTypes.string,
};

const defaultProps = {
    page: {},
    transitionTheme: null,
    isPrerender: false,
    fonts: {
        google: {
            families: ['Source+Sans+Pro:400,400i,700,700i'],
        },
    },
    inTransition: false,
    menus: [],
    menuOpen: false,
    color: null,
    theme: 'dark',
    donationUrl: null,
};

const MainLayout = ({
    page,
    transitionTheme,
    splash,
    setSplash,
    fonts,
    children,
    isPrerender,
    inTransition,
    menus,
    menuOpen,
    color,
    theme,
    setMenuOpen,
    donationUrl,
}) => {
    const splashRef = useRef(null);
    const [animate, setAnimate] = useState(false);
    const { newsletter } = useNewsletter();
    const { popup, setPopup } = usePopup(null);
    const { pathname = null } = useLocation();

    const url = page !== null ? page.url : null;
    const pagePopup = page !== null ? page.popup || 'cookies' : null;
    const notebooks = page !== null ? page.notebooks : [];
    const { loaded: fontsLoaded } = useFonts(fonts);

    const assetsLoaded = fontsLoaded || isPrerender;
    const notLoading = !!((fontsLoaded || isPrerender) && !inTransition);
    const innerStyle = {
        opacity: assetsLoaded && !inTransition ? 1 : 0,
    };

    const noHexColor = removeHex(color);
    const darkBackgroundUrl = getDarkBackground(noHexColor);
    const lightBackgroundUrl = getLightBackground(noHexColor);

    // const darkBackground = `url('${LogoImg}'), ${darkBackgroundUrl}`;
    // const lightBackground = `url('${LogoImg}'), ${lightBackgroundUrl}`;
    const currentTheme = transitionTheme || theme;
    const globalColor = currentTheme !== 'dark' ? color : '#ffffff';
    // const backgroundPath = theme !== 'dark' ? lightBackground : darkBackground;

    useEffect(() => {
        if (animate) {
            anime({
                targets: splashRef.current,
                delay: 1600,
                duration: 800,
                opacity: 0,
                complete: () => {
                    if (splashRef.current) {
                        setSplash(false);
                        if (newsletter === true && window.scrollTo) {
                            window.scrollTo({
                                top: document.body.scrollHeight,
                                behavior: 'smooth',
                            });
                        }
                    }
                },
            });
        }
        return () => {
            if (animate && splashRef.current) {
                anime.remove(splashRef.current);
            }
        };
    }, [animate, newsletter]);

    useEffect(() => {
        let id;
        if (notLoading && pathname === '/') {
            id = setTimeout(() => {
                setAnimate(true);
            }, 300);
        }
        return () => {
            clearTimeout(id);
        };
    }, [notLoading, setAnimate]);

    useEffect(() => {
        if (pathname !== '/') {
            setAnimate(false);
            setSplash(false);
        }
    }, [pathname, setSplash, setAnimate]);

    const closeMenu = useCallback(() => {
        setMenuOpen(false);
    }, []);

    const toggleMenu = useCallback(() => {
        setMenuOpen(!menuOpen);
    }, [menuOpen]);

    const menuColor = theme === 'dark' || menuOpen ? '#FFF' : color;

    // Popups

    useEffect(() => {
        setPopup(pagePopup);
    }, [pagePopup]);

    // console.log(theme, color);

    return (
        <div className={styles.container}>
            <LoadingBar color={globalColor} loading={!notLoading} loaded={notLoading} />
            <MenuMobile
                className={classNames([
                    styles.menuMobile,
                    {
                        [styles.open]: menuOpen,
                    },
                ])}
                topItems={menus.main.top}
                bottomItems={menus.main.bottom}
                color={color}
                theme="dark"
                style={{ backgroundImage: darkBackgroundUrl }}
                onClick={closeMenu}
            />
            {menuOpen ? (
                <CloseButton
                    className={styles.burger}
                    color={menuColor}
                    isActive={menuOpen}
                    onClick={toggleMenu}
                />
            ) : (
                <BurgerButton
                    className={styles.burger}
                    color={menuColor}
                    isActive={menuOpen}
                    onClick={toggleMenu}
                />
            )}
            {splash ? (
                <SplashPage
                    className={classNames([styles.splash])}
                    ref={splashRef}
                    color={color}
                    animate={pathname === '/' && animate}
                    style={{ backgroundImage: lightBackgroundUrl }}
                />
            ) : null}
            <div
                className={classNames([
                    styles.inner,
                    {
                        [styles[`${theme}`]]: theme,
                    },
                ])}
                style={{ color: globalColor }}
            >
                <Background color={color} theme={theme} />
                <div className={styles.bigLogo}>
                    <Header
                        menu={menus.header}
                        color={color}
                        theme={theme}
                        url={url}
                        donationUrl={donationUrl}
                    />
                    <div className={styles.content}>
                        <MainMenu
                            className={styles.menu}
                            color={color}
                            theme={theme}
                            topItems={menus.main.top}
                            bottomItems={menus.main.bottom}
                            url={url}
                        />
                        <div className={styles.page} style={innerStyle}>
                            {children}
                        </div>
                    </div>
                </div>
            </div>
            {notebooks && notebooks.length > 0 ? (
                <EventNotebook page={page} sections={notebooks} />
            ) : null}
            {popup !== null ? <Modals popup={popup} theme={theme} color={color} /> : null}
            <Footer menus={menus.footer} />
        </div>
    );
};

MainLayout.propTypes = propTypes;
MainLayout.defaultProps = defaultProps;

const WithStateContainer = connect(
    ({ site: { menus, isPrerender, color, theme, splash, menuOpen, donationUrl } }) => ({
        isPrerender: isPrerender || false,
        menus,
        color,
        theme,
        splash,
        menuOpen,
        donationUrl,
    }),
    (dispatch) => ({
        setSplash: (splash) => dispatch(setSplashAction(splash)),
        setMenuOpen: (open) => dispatch(setMenuOpenAction(open)),
    }),
)(MainLayout);

export default WithStateContainer;
